import { useMemo } from "react";

const C = {
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
  greenDark: "#047857",
  green: "#059669",
  greenBg: "#ECFDF5",
  amber: "#F59E0B",
  amber800: "#92400E",
  blue700: "#1D4ED8",
  blue50: "#EFF6FF",
  red: "#EF4444",
};

const PALETTE = [
  "#047857",
  "#1D4ED8",
  "#F59E0B",
  "#7C3AED",
  "#DB2777",
  "#0891B2",
  "#65A30D",
  "#DC2626",
  "#A16207",
  "#475569",
];

// ──────────────────────────────────────────────────────────
// Donut chart
// ──────────────────────────────────────────────────────────

export function DonutChart({ data, total, centerLabel }) {
  const items = (data || []).filter((d) => d.value > 0);
  const sum = total ?? items.reduce((s, d) => s + d.value, 0);
  const size = 160;
  const stroke = 22;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;

  let acc = 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
      <svg width={size} height={size} style={{ flexShrink: 0 }}>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={C.line}
          strokeWidth={stroke}
        />
        {sum > 0 &&
          items.map((d, i) => {
            const frac = d.value / sum;
            const dash = frac * circumference;
            const gap = circumference - dash;
            const offset = -acc * circumference;
            acc += frac;
            return (
              <circle
                key={i}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={d.color || PALETTE[i % PALETTE.length]}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${gap}`}
                strokeDashoffset={offset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${cx} ${cy})`}
              />
            );
          })}
        <text
          x={cx}
          y={cy - 6}
          textAnchor="middle"
          style={{ fontSize: 12, fill: C.sub, fontWeight: 600 }}
        >
          {centerLabel || "전체"}
        </text>
        <text
          x={cx}
          y={cy + 16}
          textAnchor="middle"
          style={{ fontSize: 22, fill: C.ink, fontWeight: 800 }}
        >
          {sum.toLocaleString()}
        </text>
      </svg>

      <div style={{ flex: 1, minWidth: 120, display: "flex", flexDirection: "column", gap: 8 }}>
        {items.length === 0 ? (
          <div style={{ color: C.sub, fontSize: 13 }}>데이터가 없습니다.</div>
        ) : (
          items.map((d, i) => {
            const pct = sum > 0 ? Math.round((d.value / sum) * 100) : 0;
            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: d.color || PALETTE[i % PALETTE.length],
                    flexShrink: 0,
                  }}
                />
                <span style={{ flex: 1, color: C.body, fontWeight: 600 }}>
                  {d.label}
                </span>
                <span style={{ color: C.ink, fontWeight: 700 }}>
                  {d.value.toLocaleString()}
                </span>
                <span
                  style={{
                    color: C.sub,
                    fontSize: 11,
                    width: 38,
                    textAlign: "right",
                  }}
                >
                  {pct}%
                </span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Horizontal bar chart
// ──────────────────────────────────────────────────────────

export function BarChartH({ data, color = C.greenDark }) {
  const items = data || [];
  const max = items.reduce((m, d) => Math.max(m, d.value), 0) || 1;

  if (items.length === 0) {
    return <div style={{ color: C.sub, fontSize: 13 }}>데이터가 없습니다.</div>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {items.map((d, i) => {
        const w = (d.value / max) * 100;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 90,
                fontSize: 12,
                color: C.body,
                fontWeight: 600,
                textAlign: "right",
                flexShrink: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={d.label}
            >
              {d.label}
            </div>
            <div
              style={{
                flex: 1,
                height: 22,
                background: "#F3F4F6",
                borderRadius: 6,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${w}%`,
                  height: "100%",
                  background: color,
                  borderRadius: 6,
                  transition: "width 0.4s ease",
                }}
              />
            </div>
            <div
              style={{
                width: 50,
                fontSize: 12,
                fontWeight: 700,
                color: C.ink,
                textAlign: "right",
                flexShrink: 0,
              }}
            >
              {d.value.toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Time-series line chart (last 30 days)
// data: [{date: 'YYYY-MM-DD', count: N}]
// ──────────────────────────────────────────────────────────

function fillLast30Days(data) {
  const map = new Map((data || []).map((d) => [d.date, d.count]));
  const result = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 29; i >= 0; i--) {
    const dt = new Date(today);
    dt.setDate(today.getDate() - i);
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const key = `${yyyy}-${mm}-${dd}`;
    result.push({ date: key, count: map.get(key) || 0, dt });
  }
  return result;
}

export function LineChart30d({ data, color = C.greenDark, fill = true }) {
  const series = useMemo(() => fillLast30Days(data), [data]);
  const max = series.reduce((m, d) => Math.max(m, d.count), 0) || 1;

  const W = 600;
  const H = 200;
  const padL = 30;
  const padR = 8;
  const padT = 12;
  const padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const points = series.map((d, i) => {
    const x = padL + (i / (series.length - 1)) * innerW;
    const y = padT + innerH - (d.count / max) * innerH;
    return { x, y, ...d };
  });

  const polyline = points.map((p) => `${p.x},${p.y}`).join(" ");
  const areaPath = [
    `M${points[0].x},${padT + innerH}`,
    ...points.map((p) => `L${p.x},${p.y}`),
    `L${points[points.length - 1].x},${padT + innerH}`,
    "Z",
  ].join(" ");

  // y-axis ticks
  const ticks = 4;
  const yTicks = [];
  for (let t = 0; t <= ticks; t++) {
    const v = Math.round((max * t) / ticks);
    const y = padT + innerH - (v / max) * innerH;
    yTicks.push({ y, v });
  }

  // x-axis labels (every 5 days)
  const xLabels = points
    .map((p, i) => ({ ...p, i }))
    .filter((p) => p.i % 5 === 0 || p.i === points.length - 1);

  const sum = series.reduce((s, d) => s + d.count, 0);

  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: C.sub,
          fontWeight: 600,
          marginBottom: 6,
        }}
      >
        총 {sum.toLocaleString()}건 · 일 평균 {(sum / 30).toFixed(1)}건
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
        {yTicks.map((t, i) => (
          <g key={i}>
            <line
              x1={padL}
              x2={W - padR}
              y1={t.y}
              y2={t.y}
              stroke={C.line}
              strokeDasharray="2,3"
            />
            <text
              x={padL - 4}
              y={t.y + 3}
              textAnchor="end"
              style={{ fontSize: 10, fill: C.sub }}
            >
              {t.v}
            </text>
          </g>
        ))}

        {fill && (
          <path
            d={areaPath}
            fill={color}
            opacity="0.12"
          />
        )}
        <polyline
          points={polyline}
          fill="none"
          stroke={color}
          strokeWidth="2"
        />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={p.count > 0 ? 2.5 : 1.5}
            fill={p.count > 0 ? color : C.line}
          />
        ))}

        {xLabels.map((p, i) => {
          const dt = p.dt;
          const lbl = `${dt.getMonth() + 1}/${dt.getDate()}`;
          return (
            <text
              key={i}
              x={p.x}
              y={H - 8}
              textAnchor="middle"
              style={{ fontSize: 10, fill: C.sub }}
            >
              {lbl}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Card wrapper for chart sections
// ──────────────────────────────────────────────────────────

export function ChartCard({ title, hint, children, span }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: "18px 20px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        gridColumn: span ? `span ${span}` : undefined,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: C.ink,
            letterSpacing: "-0.01em",
          }}
        >
          {title}
        </div>
        {hint && (
          <div style={{ fontSize: 11, color: C.sub, marginTop: 2 }}>{hint}</div>
        )}
      </div>
      {children}
    </div>
  );
}
